'use client';

import {
	BigNum,
	BN,
	PublicKey,
	SpotMarketConfig,
	User,
} from '@velocity-exchange/sdk';
import {
	createSwiftAccountIxIfNotExists,
	MarketId,
	MAIN_POOL_ID,
	createDepositIxs,
} from '@velocity-exchange/common';
import { useCallback } from 'react';
import { notify } from 'src/utils/notifications';
import captureException from 'src/utils/captureException';
import TransactionErrorHandler from 'src/utils/TransactionErrorHandler/TransactionErrorHandler';
import { updateAfterTx } from 'src/stores/VelocityStore/helpers';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import useVelocityAccountsStore from 'src/stores/useVelocityAccountsStore';
import { useGetTxParams } from 'src/hooks/velocityActions/transactions/useGetTxParams';
import useVelocityClient from 'src/hooks/useVelocityClient';
import invariant from 'tiny-invariant';
import { UiEnhancedIWalletV2 } from 'src/utils/wallets/IWalletV2';
import { useSponsoredSignVelocityActionWithFallback } from 'src/hooks/wallets/useSponsoredSignVelocityActionWithFallback';

export function useDepositCollateral() {
	const velocityClient = useVelocityClient();
	const walletAdapter = useVelocityStore((s) => s.wallet.current.adapter);
	const isGeoblocked = useVelocityStore((s) => s.isGeoblocked);
	const countryCode = useVelocityStore((s) => s.countryCode);

	const accountsGet = useVelocityAccountsStore((s) => s.get);
	const getTxParams = useGetTxParams();

	const depositAction = useCallback(
		async (
			wallet: UiEnhancedIWalletV2,
			userAccount: User,
			transferAmount: BigNum,
			spotMarketConfig: SpotMarketConfig,
			isMaxBorrowRepayment: boolean | undefined,
			initSwiftAccount: boolean,
			authority: PublicKey
		) => {
			invariant(velocityClient, 'driftClient is required');

			const depositIxs = await createDepositIxs({
				velocityClient: velocityClient,
				user: userAccount,
				amount: transferAmount,
				spotMarketConfig,
				isMaxBorrowRepayment,
			});

			if (initSwiftAccount && spotMarketConfig.poolId === MAIN_POOL_ID) {
				const { ix: initSwiftAccountIx } =
					await createSwiftAccountIxIfNotExists({
						velocityClient: velocityClient,
						authority,
					});

				if (initSwiftAccountIx) {
					depositIxs.push(initSwiftAccountIx);
				}
			}

			const txParams = getTxParams({
				marketId: MarketId.createSpotMarket(spotMarketConfig.marketIndex),
			});

			await wallet.signAndSendTransaction(depositIxs, txParams);
		},
		[velocityClient, getTxParams]
	);

	const wrappedDeposit =
		useSponsoredSignVelocityActionWithFallback(depositAction);

	const depositCollateralForExistingAccount = useCallback(
		async (
			amount: BigNum,
			spotMarketConfig: SpotMarketConfig,
			userKey?: string,
			isMaxBorrowRepayment?: boolean,
			initSwiftAccount = false
		) => {
			if (!velocityClient || !walletAdapter || !walletAdapter.publicKey) return;

			if (isGeoblocked && countryCode !== 'US') {
				notify({
					type: 'error',
					message: 'Geoblocked',
					description:
						'You are not allowed to use Velocity from a restricted territory',
					lengthMs: 5000,
				});
				return;
			}

			if (!userKey) {
				userKey = accountsGet().currentUserKey ?? '';
			}

			const accountsState = accountsGet();
			const accountData = accountsState.accounts[userKey];

			if (!accountData?.client) return;

			const userAccount = accountData.client;

			await velocityClient.switchActiveUser(
				accountData.userId,
				accountData.authority
			);

			const toastId = `deposit-${amount.toString()}-${Date.now()}`;

			notify({
				type: 'awaiting_wallet_confirmation',
				id: toastId,
				showUntilCancelled: true,
			});

			const spotAssetDecimals = spotMarketConfig.precisionExp;

			let amountToSend = amount;

			if (isMaxBorrowRepayment) {
				// We're doubling the amount to send here because it's a max borrow repayment (and reduce-only MUST be true) .. we do this to ensure no dust position gets left behind
				amountToSend = amountToSend.scale(2, 1);
			}

			const transferAmount = amountToSend.shiftTo(new BN(spotAssetDecimals));

			const spotMarketAccount = velocityClient.getSpotMarketAccount(
				spotMarketConfig.marketIndex
			);

			if (!spotMarketAccount) return;

			try {
				await wrappedDeposit(
					userAccount,
					transferAmount,
					spotMarketConfig,
					isMaxBorrowRepayment,
					initSwiftAccount,
					walletAdapter.publicKey
				);

				notify({
					type: 'success',
					message: 'Deposit successful.',
					id: toastId,
					updatePrevious: true,
				});

				updateAfterTx(velocityClient, userAccount);

				return true;
			} catch (e: any) {
				captureException(e);
				TransactionErrorHandler.handleError({
					error: e,
					toastId,
					wallet: walletAdapter,
				});
			}
		},
		[
			velocityClient,
			walletAdapter,
			isGeoblocked,
			countryCode,
			accountsGet,
			wrappedDeposit,
		]
	);

	return { depositCollateralForExistingAccount };
}
